/**
 * The PDF document row (ISSUE-750 R5): a file icon, the filename, and the
 * file size, the compact document row `.agents/design-prompts/
 * 50-messages.md:101-104` (Q207) asks for. The whole row is one tap target
 * that opens the PDF, reusing the browser's own viewer -- no in-app PDF
 * renderer, matching `apps/app/src/features/chatDetails/FilesTab.tsx`'s own
 * "Open" action rather than inventing a second one.
 *
 * fileName and byteSize are NOT fetched here: they arrive already resolved
 * on the message row itself (`messaging-api`'s `/message/list`, R2), the
 * same server-resolves-client-renders split `MultiPhotoBubble` follows for
 * its caption. Only the actual openable URL is resolved client side, from
 * `media-api`'s `/object/get-batch`, the one sanctioned way to turn a
 * mediaId into a servable URL. This bubble calls `useObjectGetBatch`
 * directly rather than `chatDetails/useChatMediaUrls.ts`, for the same
 * reason `MultiPhotoBubble` does not reuse that hook either: it collapses
 * "still loading" and "resolved to nothing" into one `null`, and this row
 * needs to tell those apart so a media-api outage (ISSUE-755: not deployed
 * in production yet) reads as a real, distinguishable "Unavailable" rather
 * than a silently dead tap target or a blank row.
 */
import { useObjectGetBatch } from "@sportsmet/media-sdk/private";
import type { ReactNode } from "react";

import { PDF_UNAVAILABLE_LABEL } from "./chatExtrasCopy";
import { formatFileSize } from "./fileSizeFormat";
import type { typeMessagePdf } from "./mergeMessagePages";

export type typePdfBubbleProps = {
  mediaObjectId: string;
  pdf: typeMessagePdf;
};

function DocumentIcon(): ReactNode {
  return (
    <svg
      className="h-icon-md w-icon-md"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M5 2.5h6.5L15 6v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M11.5 2.5V6H15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const PdfBubble = (props: typePdfBubbleProps): ReactNode => {
  const { mediaObjectId, pdf } = props;
  const query = useObjectGetBatch({ mediaIds: [mediaObjectId] });

  const isLoading = query.isPending;
  const isBatchFailed = query.isError || query.data?.error != null;
  const item = (query.data?.data?.items ?? []).find(
    (candidate) => candidate.mediaId === mediaObjectId,
  );
  const openUrl =
    !isLoading &&
    !isBatchFailed &&
    item?.status === "published" &&
    item.variants
      ? item.variants.original.url
      : null;
  const isUnavailable = !isLoading && openUrl === null;

  const sizeLabel = pdf.byteSize !== null ? formatFileSize(pdf.byteSize) : null;

  const inner = (
    <>
      <span
        aria-hidden="true"
        className="grid h-icon-lg w-icon-lg shrink-0 place-items-center rounded-md bg-surface-sunken text-ink-secondary"
      >
        <DocumentIcon />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className="truncate text-body text-ink"
          data-testid="pdf-bubble-filename"
        >
          {pdf.fileName}
        </span>
        {isUnavailable ? (
          <span
            className="text-caption text-ink-tertiary"
            data-testid="pdf-bubble-unavailable"
          >
            {PDF_UNAVAILABLE_LABEL}
          </span>
        ) : sizeLabel !== null ? (
          <span
            className="text-caption text-ink-tertiary"
            data-testid="pdf-bubble-size"
          >
            {sizeLabel}
          </span>
        ) : null}
      </span>
    </>
  );

  return (
    <div data-testid="pdf-bubble">
      {openUrl !== null ? (
        <a
          href={openUrl}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="pdf-bubble-open"
          className="flex items-center gap-3 rounded-md p-1 hover:bg-surface-sunken"
        >
          {inner}
        </a>
      ) : (
        <div className="flex items-center gap-3 rounded-md p-1">{inner}</div>
      )}
    </div>
  );
};
